import type { IFieldFactoryProps } from '@sravni/cosago-react-library/lib/types';
import { validateFullName } from '@sravni/cosago-react-library/lib/validationSchemes';
import { useCallback, useState } from 'react';

import { useGetFullNameSuggest } from 'shared/api/daData/dadata.query';
import { useFetchSuggestions } from 'shared/lib/useFetchSuggestions';
import type { TDadataFullNameSuggest } from 'shared/types/TDaData';
import { FullNameAutocomplete } from 'shared/ui/FullNameAutocomplete';

type Props = IFieldFactoryProps & {
  initialFioValue: string;
  onInitialSuggestsLoad?: (option: Dictionaries.TSelectOption<string, TDadataFullNameSuggest['data']>) => void;
};

export const FullNameField: FC<Props> = ({ initialFioValue, onInitialSuggestsLoad, ...props }) => {
  const [isInitialSuggestsLoaded, setIsInitialSuggestsLoaded] = useState(false);

  const [getFullNameSuggest] = useGetFullNameSuggest();

  const getSuggests = useCallback(
    async (value: string) => {
      const response = await getFullNameSuggest({ query: value }, true);

      const suggestions = response.data?.suggestions || [];

      const mappedSuggestions =
        suggestions.reduce<Array<Dictionaries.TSelectOption<string, TDadataFullNameSuggest['data']>>>((acc, item) => {
          if (item.value) {
            acc.push({ value: item.value, label: item.value, data: item.data });
          }

          return acc;
        }, []) || [];

      if (!isInitialSuggestsLoaded) {
        setIsInitialSuggestsLoaded(true);
        if (mappedSuggestions.length && initialFioValue && onInitialSuggestsLoad) {
          onInitialSuggestsLoad(mappedSuggestions[0]);
        }
      }

      return mappedSuggestions;
    },
    [isInitialSuggestsLoaded, initialFioValue, onInitialSuggestsLoad, getFullNameSuggest],
  );

  const shouldHideOnSelect = (value: Nullable<ICustomSelectValue>) => {
    try {
      validateFullName({}).validateSync(value);
      return true;
    } catch (error) {
      return false;
    }
  };

  const { isLoading, currentSuggestions, setSearchValue } = useFetchSuggestions(getSuggests, initialFioValue, true);

  return (
    <FullNameAutocomplete
      {...props}
      suggestions={currentSuggestions}
      // Добавляем пробел, чтобы при выборе suggest сразу начал предлагать следующие слова имя/отчество
      applySuggestTrigger={(value: { value?: string | number } | null) => {
        if (!value) {
          return setSearchValue('');
        }

        if (!!value && !value.value) {
          return setSearchValue('');
        }

        return setSearchValue(`${value.value} `);
      }}
      onSearch={setSearchValue}
      isSuggestionsLoading={isLoading}
      shouldHideSuggestionsList={shouldHideOnSelect}
    />
  );
};
